import { ComponentsCustomer } from "@omni/types/components/customer/customer.model";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { uniqBy } from "lodash-es";
import { createContext, use, useEffect, useMemo } from "react";
import { shallowEqual } from "react-redux";
import { useSelector } from "react-redux/es/hooks/useSelector";

import { Spinner } from "@/components/General/Spinner/spinner.component";
import { useThunkDispatch } from "@/redux/dispatcher";
import * as AddressBookModule from "@/redux/modules/api/data/addressbook/addressbook.module";
import { BoxConditionModel } from "@/redux/modules/api/data/cart/@types/cart.model";
import * as CustomerModule from "@/redux/modules/api/data/customer/customer.module";
import { getCustomer } from "@/redux/modules/api/data/customer/customer.selectors";
import * as PhonenumberPrefixesModule from "@/redux/modules/api/data/phonenumberPrefixes/phonenumberPrefixes.module";
import * as PhonenumberPrefixesSelectors from "@/redux/modules/api/data/phonenumberPrefixes/phonenumberPrefixes.selectors";

import { useCheckout } from "../useCheckout.hook";
import { TermsOfServiceCheckDocument } from "./termsOfService/termsOfServiceCheck.schema";

type CheckoutCustomerContextValue = {
    boxContestConditionInfo: string;
    boxPartnerConditionMicroCopy: string;
    activeBoxConditions: Array<BoxConditionModel>;
    additionalServiceProductDocuments: Array<TermsOfServiceCheckDocument>;
    customer?: ComponentsCustomer | null;
};

type CheckoutCustomerContextProviderProps = RequireChildrenFor<{
    boxContestConditionInfo: string;
    boxPartnerConditionMicroCopy: string;
}>;

export const CheckoutCustomerContext = createContext<
    CheckoutCustomerContextValue | undefined
>(undefined);
CheckoutCustomerContext.displayName = "CheckoutCustomerContext";

export const CheckoutCustomerContextProvider = ({
    boxContestConditionInfo,
    boxPartnerConditionMicroCopy,
    children
}: CheckoutCustomerContextProviderProps) => {
    const { cart } = useCheckout();
    const dispatch = useThunkDispatch();

    const customer = useSelector(getCustomer);

    const phonenumberPrefixes = useSelector(
        PhonenumberPrefixesSelectors.getPhonenumberPrefixes,
        shallowEqual
    );

    useEffect(() => {
        if (phonenumberPrefixes.length === 0) {
            dispatch(PhonenumberPrefixesModule.load());
        }
    }, [dispatch, phonenumberPrefixes]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        dispatch(AddressBookModule.load());
        dispatch(CustomerModule.load());
    }, []);

    const additionalServiceProductDocuments = useMemo(() => {
        // Unique additional service products that are in the cart and the top level additional services
        const parentAdditionalServices = uniqBy(
            cart?.items.flatMap(({ additionalServices, title }) =>
                additionalServices
                    .filter(
                        ({ childAdditionalService }) => !childAdditionalService
                    )
                    .map(({ sku }) => ({ sku: sku, title: title }))
            ) ?? [],
            ({ sku }) => sku
        );

        // All the possible montages from products in the cart - also from non-included additional services
        const montages = cart?.items.flatMap(
            (cartItem) => cartItem.product?.references?.montages ?? []
        );

        return parentAdditionalServices
            .flatMap(
                (parentAdditionalService) =>
                    montages
                        ?.find(({ sku }) => parentAdditionalService.sku === sku)
                        ?.documents?.map((document) => ({
                            document: document,
                            title: parentAdditionalService.title
                        })) ?? []
            )
            .filter(({ document }) => isDefined(document))
            .map<TermsOfServiceCheckDocument>(({ document, title }) => ({
                title: title,
                file: document,
                checked:
                    !!cart?.acceptedAdditionalServiceDocumentCodes.includes(
                        document.code
                    )
            }));
    }, [cart]);

    const canRender = cart && customer && phonenumberPrefixes.length > 0;

    return (
        <CheckoutCustomerContext
            value={{
                boxContestConditionInfo,
                boxPartnerConditionMicroCopy,
                activeBoxConditions: cart?.activeBoxConditions ?? [],
                additionalServiceProductDocuments,
                customer
            }}
        >
            {!canRender && <Spinner />}
            {canRender && children}
        </CheckoutCustomerContext>
    );
};

export const useCheckoutCustomerContext = () => {
    const context = use<CheckoutCustomerContextValue | undefined>(
        CheckoutCustomerContext
    );

    if (!isDefined(context)) {
        throw new Error(`${CheckoutCustomerContext.displayName} is undefined.`);
    }

    return context;
};
